import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Маршрут приложения.
 *
 * Роутер в зависимости не тянем: экранов немного, а адрес партии обязан
 * оставаться обычной ссылкой, которой делятся, — и кнопка «назад» при этом
 * должна работать. Хватает pathname и history.
 */
export type Route =
  | { name: 'lobby' }
  | { name: 'game'; id: string }
  | { name: 'auth' }
  | { name: 'reset' }
  | { name: 'friends' }
  | { name: 'profile' }
  | { name: 'leaders' }
  | { name: 'rules' }
  | { name: 'settings' }
  | { name: 'shop' }
  | { name: 'user'; id: number }

const GAME = /^\/g\/([0-9a-f-]{36})$/i
const USER = /^\/u\/(\d+)$/

function parse(path: string): Route {
  const game = GAME.exec(path)

  if (game !== null) return { name: 'game', id: game[1] }

  const user = USER.exec(path)

  if (user !== null) return { name: 'user', id: Number(user[1]) }

  if (path === '/login') return { name: 'auth' }
  // Адрес из письма о восстановлении пароля: токен и почта в параметрах.
  if (path === '/reset') return { name: 'reset' }
  if (path === '/friends') return { name: 'friends' }
  if (path === '/me') return { name: 'profile' }
  if (path === '/leaders') return { name: 'leaders' }
  if (path === '/rules') return { name: 'rules' }
  if (path === '/settings') return { name: 'settings' }
  if (path === '/shop') return { name: 'shop' }

  // Всё остальное — главная. Сюда же попадают прежние адреса шагов сбора
  // партии (/new/...): шагов больше нет, а ссылки на них могли остаться в
  // закладках и истории.
  return { name: 'lobby' }
}

/**
 * Что показывается ПОВЕРХ экрана, а не вместо него.
 *
 * Правила, настройки, профиль, друзья и лидеры — всё это заходы «на минуту»:
 * глянуть счёт, убавить звук, посмотреть, кто в сети и кто впереди. Уводить
 * за этим с доски значит уводить с хода, а доску нельзя ни размонтировать,
 * ни перерисовать — в ней живут приближение и очередь показа ходов.
 *
 * Чужой профиль (`user`) — здесь же: в него уходят из друзей, и возвращаться
 * оттуда надо к друзьям, а не к тому, что было под ними.
 */
const OVERLAY: ReadonlySet<Route['name']> = new Set([
  'rules',
  'settings',
  'profile',
  'friends',
  'leaders',
  'shop',
  'user',
])

export function isOverlay(route: Route): boolean {
  return OVERLAY.has(route.name)
}

export function useRoute(): {
  route: Route
  /**
   * Экран под накладкой: тот, с которого её открыли.
   *
   * Правила и настройки — адреса (ссылкой на правила делятся, а «назад»
   * обязана их закрывать), но показываются они модальным окном поверх того,
   * что человек и так смотрел. Значит под ними должен остаться прежний экран,
   * а не подставленное лобби: открыв настройки посреди партии, доску нельзя
   * ни размонтировать, ни перерисовать — в ней живут приближение и очередь
   * показа ходов.
   *
   * Открывшему накладку по прямой ссылке подставляется лобби: под ней ничего
   * не было, а пустой экран за модальным окном выглядит поломкой.
   */
  base: Route
  go: (path: string) => void
  back: () => void
} {
  const [route, setRoute] = useState<Route>(() => parse(window.location.pathname))
  const base = useRef<Route>({ name: 'lobby' })

  // Присваивание в рендере, а не в эффекте: экран под накладкой нужен в том
  // же кадре, в котором она открылась, иначе он мигнёт лобби. Действие
  // идемпотентное, поэтому повторный рендер StrictMode ничего не портит.
  if (!isOverlay(route)) base.current = route

  /*
   * Сколько своих переходов лежит в истории.
   *
   * Нужно одному решению: безопасна ли history.back(). Точность здесь
   * приблизительная — «вперёд» браузера счётчик тоже уменьшит, — но цена
   * ошибки мала: вместо возврата человек окажется на лобби, а не за
   * пределами сайта.
   */
  const pushes = useRef(0)

  useEffect(() => {
    const onPop = () => {
      pushes.current = Math.max(0, pushes.current - 1)
      setRoute(parse(window.location.pathname))
    }
    window.addEventListener('popstate', onPop)

    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const go = useCallback((path: string) => {
    window.history.pushState({}, '', path)
    pushes.current += 1
    setRoute(parse(path))
  }, [])

  /*
   * «← назад» и закрытие накладки — это history.back(), а не переход на адрес
   * шага выше.
   *
   * Переходом история копилась бы: меню → сложность → меню, и следующее
   * нажатие браузерной «назад» возвращало бы человека на «сложность», откуда
   * он только что ушёл. С back() обе кнопки — своя и браузерная — делают одно
   * и то же, а это и было целью.
   *
   * Пришедшему по прямой ссылке возвращаться некуда, и уводить его с сайта
   * своей же кнопкой «закрыть» нельзя. Ему адрес ЗАМЕНЯЕТСЯ на лобби:
   * replaceState, а не push, иначе следующая «назад» снова открыла бы то, что
   * только что закрыли.
   */
  const back = useCallback(() => {
    if (pushes.current > 0) {
      window.history.back()

      return
    }

    window.history.replaceState({}, '', '/')
    setRoute(parse('/'))
  }, [])

  return { route, base: base.current, go, back }
}
